import { useState } from 'react';

import { useTranslation } from 'react-i18next';

import AddRoundedIcon from '@mui/icons-material/AddRounded';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import {
  Box,
  Button,
  Chip,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';

import { EmptyState, ErrorState, LoadingState } from '@/shared/ui';

import { useCreateMarkdownPlan, useMarkdownPlans } from '../../hooks';
import type { ToastSeverity } from '../../lib/useToast';
import { MARKDOWN_PLAN_STATUS_LABELS, markdownPlanStatusColor } from '../../model/options';
import type { CreateMarkdownPlanRequest } from '../../model/markdownPlanTypes';
import { MarkdownPlanDetailView } from './MarkdownPlanDetailView';
import { MarkdownPlanFormDialog } from './MarkdownPlanFormDialog';

export interface MarkdownPlansPanelProps {
  /** `catalog.pricing.manage` — create a plan. */
  canManage: boolean;
  /** `catalog.pricing.bulk` — activate/cancel (mass-change actions), required IN ADDITION TO `canManage`. */
  canBulk: boolean;
  onResult: (severity: ToastSeverity, message: string) => void;
}

/** The "Markdown plans" sub-view (design spec §8.9) — markdown/clearance plans: list, create
 * dialog, and an always-read-only detail (no update endpoint on the live BFF contract). Rendered
 * inline, like bulk batches — no routed sub-page. */
export function MarkdownPlansPanel({ canManage, canBulk, onResult }: MarkdownPlansPanelProps) {
  const { t } = useTranslation('catalog');
  const [createOpen, setCreateOpen] = useState(false);
  const [selectedPlanId, setSelectedPlanId] = useState<string | null>(null);

  // The shipped BFF gates every path on this tag — including the plain GET list — behind
  // `catalog.pricing.bulk` (`access.requirePricingBulk()` on every method of
  // `CatalogPricingControllerImpl`'s markdown-plans block, GETs included). Disabling the query when
  // the caller lacks it avoids firing a request that can only ever 403, and lets the render below
  // show a clean "you don't have this permission" state instead of a generic load-failure ErrorState.
  const plansQuery = useMarkdownPlans({ page: 0, size: 50 }, { enabled: canBulk });
  const createMutation = useCreateMarkdownPlan();

  if (selectedPlanId) {
    return (
      <MarkdownPlanDetailView
        planId={selectedPlanId}
        canManage={canManage && canBulk}
        onBack={() => setSelectedPlanId(null)}
        onResult={onResult}
      />
    );
  }

  const canCreate = canManage && canBulk;
  const createButton = (
    <Button variant="contained" startIcon={<AddRoundedIcon />} disabled={!canCreate} onClick={() => setCreateOpen(true)}>
      {t('markdownPlansPanel.create')}
    </Button>
  );

  const handleCreate = (body: CreateMarkdownPlanRequest) => {
    createMutation.mutate(body, {
      onSuccess: (created) => {
        setCreateOpen(false);
        setSelectedPlanId(created.id);
        onResult('success', t('markdownPlansPanel.created'));
      },
      onError: (error) => onResult('error', error.message),
    });
  };

  return (
    <Box>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', sm: 'center' }} justifyContent="flex-end" sx={{ mb: 2 }}>
        <Tooltip title={t('markdownPlansPanel.refreshAria')}>
          <IconButton aria-label={t('markdownPlansPanel.refreshAria')} onClick={() => void plansQuery.refetch()}>
            <RefreshRoundedIcon />
          </IconButton>
        </Tooltip>
        {canCreate ? (
          createButton
        ) : (
          <Tooltip title={t('bulkBatchesPanel.noBulkPermission')}>
            <span>{createButton}</span>
          </Tooltip>
        )}
      </Stack>

      {!canBulk ? (
        <EmptyState
          icon={<LockOutlinedIcon sx={{ fontSize: 48 }} />}
          title={t('bulkBatchesPanel.noBulkPermissionTitle')}
          description={t('bulkBatchesPanel.noBulkPermission')}
        />
      ) : plansQuery.isLoading ? (
        <LoadingState label={t('markdownPlansPanel.loading')} />
      ) : plansQuery.isError ? (
        <ErrorState message={t('markdownPlansPanel.loadError')} onRetry={() => void plansQuery.refetch()} />
      ) : (plansQuery.data ?? []).length === 0 ? (
        <EmptyState
          title={t('markdownPlansPanel.empty.title')}
          description={t('markdownPlansPanel.empty.description')}
          action={canCreate ? <Button variant="contained" startIcon={<AddRoundedIcon />} onClick={() => setCreateOpen(true)}>{t('markdownPlansPanel.create')}</Button> : undefined}
        />
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('markdownPlansPanel.table.name')}</TableCell>
                <TableCell>{t('markdownPlansPanel.table.status')}</TableCell>
                <TableCell align="right">{t('markdownPlansPanel.table.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(plansQuery.data ?? []).map((plan) => (
                <TableRow key={plan.id} hover sx={{ cursor: 'pointer' }} onClick={() => setSelectedPlanId(plan.id)}>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>
                      {plan.name}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip size="small" color={markdownPlanStatusColor(plan.status)} label={t(MARKDOWN_PLAN_STATUS_LABELS[plan.status] ?? plan.status)} />
                  </TableCell>
                  <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                    {/* Activate/cancel are NOT quick row actions on purpose — both are confirmed,
                        consequence-laden transitions (design spec §8.9's own confirm copy
                        describes real schedule effects), so this "open" action is the single row
                        affordance; the confirmed activate/cancel buttons live on the detail view. */}
                    <Tooltip title={t('markdownPlansPanel.openAction')}>
                      <IconButton size="small" aria-label={t('markdownPlansPanel.openActionAria', { name: plan.name })} onClick={() => setSelectedPlanId(plan.id)}>
                        <OpenInNewRoundedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <MarkdownPlanFormDialog open={createOpen} pending={createMutation.isPending} onClose={() => setCreateOpen(false)} onSave={handleCreate} />
    </Box>
  );
}
